<script setup>
import { useStore } from "vuex";
import interactjs from "interactjs";
import { onMounted, ref, watch, computed } from "vue";
import { isMobile } from "common/utils/browser";

const emit = defineEmits(["update:zoom"]);
const props = defineProps({
  artworkSize: {
    type: Object,
    required: true
  },
  imageUrl: {
    type: String,
    required: true
  },
  bgStyle: {
    type: Object,
    default: null
  },
  initWidth: {
    type: Number,
    required: true
  }
});

const store = useStore();
const enhanced = computed(() => store.getters["artwork/enhanced"]);

const interact = ref(null);
const wrapperRef = ref(null);
const reviewImageRef = ref(null);
const style = ref(null);
const position = ref({ x: 0, y: 0 });
const calculateAspectRatio = () =>
  new Promise(resolve => {
    const image = new Image();
    image.src = props.imageUrl;
    image.onload = () => {
      const ratio = image.width / image.height;
      resolve({
        ratio,
        imageWidth: image.width,
        imageHeight: image.height
      });
    };
  });

const resetState = () => {
  style.value = null;
  position.value = { x: 0, y: 0 };
};

const resizeMoveListener = ({ rect }) => {
  const { width, height } = rect;
  resetState();
  style.value = { width: `${width}px`, height: `${height}px` };
  emit("update:zoom", width / props.initWidth);
};

const dragMoveListener = event => {
  position.value = {
    x: position.value.x + event.dx,
    y: position.value.y + event.dy
  };
  style.value = {
    ...style.value,
    transform: `translate(${position.value.x}px, ${position.value.y}px)`
  };
};

const initDefaultSize = async () => {
  const { imageWidth } = await calculateAspectRatio();
  const { clientWidth } = wrapperRef.value;
  const width = imageWidth > clientWidth ? clientWidth : imageWidth;
  emit("update:zoom", width / props.initWidth);
};

const getMinSize = async () => {
  const { ratio, imageWidth, imageHeight } = await calculateAspectRatio();
  const { offsetWidth, offsetHeight } = wrapperRef.value;
  return ratio < 1
    ? { width: (imageWidth * offsetHeight) / imageHeight, height: offsetHeight }
    : { width: offsetWidth, height: (imageHeight * offsetWidth) / imageWidth };
};

const initResize = async () => {
  const { ratio, imageWidth, imageHeight } = await calculateAspectRatio();
  interact.value = interactjs(reviewImageRef.value).resizable({
    edges: { left: true, right: true, bottom: true, top: true },
    listeners: { move: resizeMoveListener },
    restrictEdges: { outer: "parent", endOnly: true },
    modifiers: [
      interactjs.modifiers.aspectRatio({ ratio }),
      interactjs.modifiers.restrictSize({
        min: await getMinSize(),
        max: { width: imageWidth, height: imageHeight }
      })
    ]
  });
};

const initDrag = () => {
  interact.value = interactjs(reviewImageRef.value).draggable({
    listeners: { move: dragMoveListener },
    modifiers: [
      interactjs.modifiers.restrictRect({
        restriction: "parent"
      })
    ]
  });
};

const initGestural = () => {
  interact.value = interactjs(reviewImageRef.value).gesturable({
    listeners: {
      move(event) {
        resizeMoveListener({
          rect: {
            width: event.rect.width * event.scale,
            height: event.rect.height * event.scale
          }
        });
        dragMoveListener(event);
      }
    }
  });
};

const changeSizeListener = async () => {
  const { imageWidth, imageHeight } = await calculateAspectRatio();
  const { width, height } = style.value || { width: imageWidth, height: imageHeight };
  if (width === `${imageWidth}px` && height === `${imageHeight}px`) {
    resizeMoveListener({ rect: await getMinSize() });
  } else {
    resizeMoveListener({ rect: { width: imageWidth, height: imageHeight } });
  }
};

const initEvents = () => {
  interact.value = interactjs(reviewImageRef.value).off("tap", changeSizeListener);

  if (!isMobile()) {
    interact.value = interactjs(reviewImageRef.value).on("tap", changeSizeListener);
  }
};

const initCanvas = async () => {
  resetState();
  await initResize();
  await initDefaultSize();
  initDrag();
  initGestural();
  initEvents();
};

const handleSizeChange = (newValue, oldValue) => {
  let editType = "Print as uploaded";

  if (newValue.width && newValue.height && oldValue.width && oldValue.height) {
    editType = "Size Adjustment";
  } else if (enhanced.value) {
    editType = "Jiffy Print Perfect";
  }

  store.commit("artwork/setEditType", editType);
};

watch(
  () => props.artworkSize,
  (newValue, oldValue) => {
    if (!(newValue.width && newValue.height)) return;

    handleSizeChange(newValue, oldValue);
    initCanvas();
  }
);
onMounted(initCanvas);
</script>

<template>
  <div ref="wrapperRef" class="image-review-image" :style="bgStyle">
    <div ref="reviewImageRef" class="image-review-image__handler" :style="style">
      <img class="image-review-image__image" :src="props.imageUrl" alt="Artwork canvas" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.image-review-image {
  height: 100%;
  user-select: none;
  overflow: hidden;
  background-color: #fff;

  &__handler {
    touch-action: none;
  }

  &__image {
    touch-action: none;
    pointer-events: none;

    @include with-desktop {
      margin: 0 auto;
      max-height: 100%;
    }
  }
}
</style>
